import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { PersonHelpers, PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { InsuranceTabHooks, InsuranceStatusChipButton, InsuranceDetailsConstants } from '@frontend/insurance-tab';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { getFullName } from '@frontend/name';
import { useContactPanelShallowStore } from '@frontend/shared';
import { getPetAge, useGetActivePetsQuery } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ChipVariants,
  NakedButton,
  SpinningLoader,
  Text,
  Truncated,
  useModalControl,
} from '@frontend/design-system';
import { useScheduleActionsContainerMethod } from '../../../context/ScheduleActionsContainerContext';
import { ScheduleEventPersonInfo } from '../helpers';
import { AppointmentEventActionButtons } from './appointment-event-action-buttons';

const InfoText = ({ text }: { text: string }) =>
  text ? (
    <Text color='light' size='medium'>
      {text}
    </Text>
  ) : null;

const PersonStatus = ({ status }: { status: string }) => {
  if (status === PersonStatus_Enum.ACTIVE) return;

  const STATUS_VARIANT: Partial<Record<PersonStatus_Enum, Partial<ChipVariants>>> = {
    [PersonStatus_Enum.PROSPECTIVE]: 'rose',
    [PersonStatus_Enum.ACTIVE]: 'success',
    [PersonStatus_Enum.INACTIVE]: 'neutral',
  };

  const chipVariant = status ? STATUS_VARIANT[status.toUpperCase() as PersonStatus_Enum] : undefined;

  return chipVariant ? (
    <Chip variant={chipVariant} css={{ textTransform: 'capitalize' }}>
      {status.toLowerCase()}
    </Chip>
  ) : (
    <InfoText text={status} />
  );
};

interface Props {
  locationId: string;
  personInfo: ScheduleEventPersonInfo;
  appointmentId?: string;
  actionsToolbar?: React.ReactNode;
  closeAppointmentDetailsModal?: () => void;
  serviceableIds: string[];
}

export const PersonDetails = ({
  personInfo: { id, name, status, gender, age },
  locationId,
  appointmentId,
  actionsToolbar,
  serviceableIds,
  closeAppointmentDetailsModal,
}: Props) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { setPersonId, setScrollTo, setSelectedTab } = useContactPanelShallowStore(
    'setPersonId',
    'setScrollTo',
    'setSelectedTab'
  );
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const [defaultDateRange, setDefaultDateRange] = useState<InsuranceDetailsConstants.DateRangeOptionValue>('today');
  const verifyInsuranceModalControl = useModalControl();
  const verificationMetricModalControl = useModalControl();
  const { closeModal: closeScheduleActionsModal } = useScheduleActionsContainerMethod();

  const { data: personInfoData, isLoading: isLoadingPersonInfo } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    { locationIds: [locationId], personId: id ?? '' },
    {
      enabled: !isScheduleV3FlagEnabled,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  const hasAccessToInsuranceVerification = hasSchemaACL(locationId, [
    Permission.INSURANCE_VERIFICATION_READ,
    Permission.INSURANCE_VERIFICATION_WRITE,
  ]);

  const handleInsuranceStatusClick = () => {
    handlePersonClick(true);
    setSelectedTab('insurance-tab');
  };

  const handlePersonClick = (isScrollToInsuranceDetails: boolean) => {
    setPersonId(id, true, locationId);
    closeScheduleActionsModal?.();
    closeAppointmentDetailsModal?.();
    setSelectedTab('overview-tab');
    if (isScrollToInsuranceDetails) {
      setScrollTo('insurance-details');
    }
  };

  const { Modal: VerificationModal } = InsuranceTabHooks.useGetInsuranceVerificationModal({
    verifyInsuranceModalControl,
    verificationMetricModalControl,
    defaultDateRange,
    setDefaultDateRange,
    personInfo: personInfoData,
    locationId,
  });

  // Use first name and last name instead of preferred name
  const personName = useMemo(() => {
    if (!!(personInfoData?.FirstName || personInfoData?.LastName)) {
      return getFullName(personInfoData);
    }
    return name;
  }, [personInfoData, name]);

  const personAge = useMemo(() => {
    if (!!personInfoData?.Birthdate) {
      const personAge = PersonHelpers.getAge(personInfoData);
      return personAge ? t('{{count}} yrs', { count: Number(personAge) }) : '';
    }
    return age;
  }, [personInfoData, age]);

  return (
    <section css={containerStyle}>
      <div css={{ width: '100%' }}>
        {lastUsedVertical === 'VET' ? (
          <VetLocationHeader
            onClick={() => handlePersonClick(false)}
            closeAppointmentDetailsModal={closeAppointmentDetailsModal}
            personName={personName}
            locationId={locationId}
            personId={id}
            serviceableIds={serviceableIds}
          >
            {hasAccessToInsuranceVerification && (
              <InsuranceStatusChipButton
                locationId={locationId}
                personId={id}
                onClick={handleInsuranceStatusClick}
                handleVerifyStatusClick={verifyInsuranceModalControl.openModal}
                isLoading={isLoadingPersonInfo}
              />
            )}
          </VetLocationHeader>
        ) : (
          <Header
            onClick={() => handlePersonClick(false)}
            closeModal={() => closeAppointmentDetailsModal?.()}
            name={personName}
            locationId={locationId}
            personId={id}
            status={status || personInfoData?.Status || ''}
            gender={gender || personInfoData?.Gender || ''}
            appointmentId={appointmentId}
            age={personAge}
          >
            {hasAccessToInsuranceVerification && (
              <InsuranceStatusChipButton
                locationId={locationId}
                personId={id}
                onClick={handleInsuranceStatusClick}
                handleVerifyStatusClick={verifyInsuranceModalControl.openModal}
                isLoading={isLoadingPersonInfo}
              />
            )}
          </Header>
        )}
      </div>
      {actionsToolbar}
      {!isLoadingPersonInfo && VerificationModal}
    </section>
  );
};

type AppointmentEventCardHeaderProps = {
  onClick: () => void;
  name: string;
  locationId: string;
  personId: string;
  status: string;
  gender: string;
  age: string;
  children: React.ReactNode;
  appointmentId?: string;
  closeModal: () => void;
};

const Header = ({
  onClick,
  name,
  locationId,
  personId,
  status,
  gender,
  age,
  children,
  appointmentId,
  closeModal,
}: AppointmentEventCardHeaderProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const [firstName, restName] = name.split(/\s(.*)/s);
  const genderText = ['Unknown', 'Other'].includes(gender) ? t('{{gender}} gender', { gender }) : gender;

  return (
    <>
      <div css={headerContainerStyles}>
        <NakedButton onClick={onClick} css={{ maxWidth: 360 }}>
          <Truncated
            as='h1'
            css={{
              fontWeight: theme.font.weight.regular,
              fontSize: theme.fontSize(32),
            }}
          >
            <strong>{firstName}</strong> {restName}
          </Truncated>
        </NakedButton>
        <AppointmentEventActionButtons
          locationId={locationId}
          personId={personId}
          appointmentId={appointmentId}
          closeModal={closeModal}
        />
      </div>
      <div css={infoContainerStyles}>
        {status && <PersonStatus status={status} />}
        {gender && <InfoText text={genderText} />}
        {age && <InfoText text={age} />}
        {children}
      </div>
    </>
  );
};

const VetLocationHeader = ({
  personName,
  locationId,
  serviceableIds,
  personId,
  children,
  closeAppointmentDetailsModal,
}: {
  personName: string;
  locationId: string;
  serviceableIds: string[];
  personId: string;
  children: React.ReactNode;
  onClick: () => void;
  closeAppointmentDetailsModal?: () => void;
}) => {
  const { closeModal: closeScheduleActionsModal } = useScheduleActionsContainerMethod();
  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const petQuery = useGetActivePetsQuery({
    locationId: locationId || '',
    personId: personId || '',
  });
  const pet = petQuery?.data?.filter((pet) => pet.petServiceableId === serviceableIds?.[0])[0];
  const petAge = getPetAge(pet?.birthdate || '');

  const openContactPanel = () => {
    setPersonId(personId, true, locationId);
    closeScheduleActionsModal?.();
    closeAppointmentDetailsModal?.();
  };

  return petQuery?.isLoading ? (
    <SpinningLoader />
  ) : (
    <>
      <div css={headerContainerStyles}>
        <div css={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
          <NakedButton trackingId='vet-appointment-header-open-contact-panel' onClick={openContactPanel}>
            <FirstWordBoldText as='h1' css={{ fontSize: theme.fontSize(36), lineHeight: 1 }}>
              {pet?.name || ''}
            </FirstWordBoldText>
          </NakedButton>
          <Chip.Person avatar={<Avatar onClick={openContactPanel} name={personName || ''} size='xxs' />}>
            {personName || ''}
          </Chip.Person>
        </div>
        <AppointmentEventActionButtons
          locationId={locationId}
          personId={personId}
          closeModal={closeAppointmentDetailsModal}
        />
      </div>
      <div css={infoContainerStyles}>
        <InfoText text={pet?.classification || ''} />
        <InfoText text={pet?.breed || ''} />
        <InfoText text={pet?.gender || ''} />
        <InfoText text={petAge} />
        {children}
      </div>
    </>
  );
};

const containerStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
});

const headerContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const infoContainerStyles = css({
  display: 'flex',
  gap: theme.spacing(2),
  height: theme.spacing(4),
  alignItems: 'center',
});
